import type { MouseEvent } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ListItem from "@mui/material/ListItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import { useTheme } from "@mui/material/styles";
import { t } from "i18next";

import { useRouter } from "src/routes/hooks";
import { playerNotificationsMarkOneRead } from "src/api/user";
import { useLocales } from "src/locales/use-locales";
import { fToNow } from "src/utils/format-time";
import { resolvePlayerNotificationAction } from "src/utils/notification-action";
import type { IPlayerNotification } from "src/types/notification";

import Iconify from "src/components/iconify";

type NotificationItemProps = {
  notification: IPlayerNotification;
  refetchNotifications: () => Promise<unknown>;
  onClose: () => void;
};

const MONTHLY_ACTIVITY_PREFIX = "notification_activity_last_month_";
const BOOKING_SPENT_PREFIX = "notification_booking_spent_last_month_";
const TOTAL_SPENT_PREFIX = "notification_total_spent_last_month_";

type ParsedNotificationContent =
  | { kind: "i18n"; key: string; params?: Record<string, string | number> }
  | { kind: "raw"; text: string };

export default function NotificationItem({
  notification,
  refetchNotifications,
  onClose,
}: NotificationItemProps) {
  const theme = useTheme();
  const router = useRouter();
  const { currentLang } = useLocales();
  const action = resolvePlayerNotificationAction(notification);

  const handleMarkOneAsRead = async () => {
    if (notification.status !== 1) return;

    try {
      await playerNotificationsMarkOneRead({
        notification_id: notification.id,
      });
      await refetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

  const handleOpenNotification = async () => {
    if (!action) return;

    await handleMarkOneAsRead();
    onClose();
    router.push(action.href);
  };

  const handleMarkButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    void handleMarkOneAsRead();
  };

  const parsed = parseNotificationContent(
    notification.type,
    notification.content,
  );
  const primaryText =
    parsed.kind === "raw" ? parsed.text : t(parsed.key, parsed.params);
  const { typographyProps } = getPrimaryTextProps(notification.type);

  const renderAvatar = (
    <ListItemAvatar>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: "background.neutral",
        }}
      >
        <Box
          component="img"
          src={`/assets/icons/notification/${
            notification.type === "message" ? "ic_chat" : "ic_mail"
          }.svg`}
          sx={{ width: 24, height: 24 }}
        />
      </Stack>
    </ListItemAvatar>
  );

  const showSenderTitle =
    notification.type === "message" ||
    notification.type === "team_invitation" ||
    notification.type.startsWith("inactivity_recovery_") ||
    notification.type.startsWith("provider_post_notif_");

  const itemContent = (
    <>
      {renderAvatar}

      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        {showSenderTitle && (
          <ListItemText
            primary={`${t(
              notification.type === "message" ? "message" : "notification",
            )}: ${notification.sender_name || ""}`}
            secondary=""
          />
        )}

        <ListItemText
          disableTypography
          primary={
            <Typography {...typographyProps} color={theme.palette.common.black}>
              {primaryText}
            </Typography>
          }
          secondary={
            <Stack
              direction="row"
              alignItems="center"
              sx={{ typography: "caption", color: "text.disabled" }}
            >
              {fToNow(notification.created_at, currentLang.adapterLocale)}
            </Stack>
          }
        />
      </Stack>

      <Stack direction="row" alignItems="center">
        {notification.status === 1 && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "success.main",
            }}
          />
        )}

        {notification.status === 1 && (
          <IconButton
            size="small"
            color="primary"
            aria-label={t("mark_all_as_read")}
            onClick={handleMarkButtonClick}
          >
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        )}
      </Stack>
    </>
  );

  const itemSx = {
    p: 2.5,
    alignItems: "flex-start",
    borderBottom: "dashed 1px",
    borderColor: "divider",
  } as const;

  if (!action) {
    return <ListItem sx={itemSx}>{itemContent}</ListItem>;
  }

  return (
    <ListItemButton
      component="li"
      sx={itemSx}
      onClick={() => void handleOpenNotification()}
    >
      {itemContent}
    </ListItemButton>
  );
}

function isMultiLineNotification(type: string): boolean {
  return (
    type === "systemmessage" ||
    type.startsWith("inactivity_recovery_") ||
    type === "notification_activity_last_month" ||
    type === "notification_booking_spent_last_month" ||
    type === "notification_total_spent_last_month"
  );
}

function getPrimaryTextProps(type: string) {
  const multiline = isMultiLineNotification(type);

  return {
    typographyProps: multiline
      ? { variant: "body2" as const }
      : {
          noWrap: true,
          variant: "body2" as const,
          sx: {
            maxWidth: { xs: "250px", sm: "300px" },
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        },
  };
}

export function parseSuffixAmountCredit(
  content: string,
  prefix: string,
): { amount: number; credit: number } | null {
  if (!content.startsWith(prefix)) return null;

  const suffix = content.slice(prefix.length);
  const match = suffix.match(/^(\d+(?:\.\d+)?)_\(\s*(\d+(?:\.\d+)?)\s*\)$/);
  if (!match) return null;

  const amount = Number(match[1]);
  const credit = Number(match[2]);

  if (!Number.isFinite(amount) || !Number.isFinite(credit)) return null;

  return { amount, credit };
}

function parseSuffixNumber(content: string, prefix: string): number | null {
  if (!content.startsWith(prefix)) return null;

  const value = Number(content.slice(prefix.length));
  return Number.isFinite(value) ? value : null;
}

function parseNotificationContent(
  type: string,
  content: string,
): ParsedNotificationContent {
  if (["championship_result_reminder", "championship_result_escalation"].includes(type)) {
    return { kind: "i18n", key: type, params: {} };
  }
  if (type === "notification_activity_last_month") {
    const hours = parseSuffixNumber(content, MONTHLY_ACTIVITY_PREFIX);
    if (hours !== null) {
      return {
        kind: "i18n",
        key: "notification_activity_last_month",
        params: { hours },
      };
    }
  }

  if (type === "notification_booking_spent_last_month") {
    const parsed = parseSuffixAmountCredit(content, BOOKING_SPENT_PREFIX);
    if (parsed) {
      return {
        kind: "i18n",
        key: "notification_booking_spent_last_month",
        params: {
          amount: parsed.amount,
          credit: parsed.credit,
        },
      };
    }
  }

  if (type === "notification_total_spent_last_month") {
    const amount = parseSuffixNumber(content, TOTAL_SPENT_PREFIX);
    if (amount !== null) {
      return {
        kind: "i18n",
        key: "notification_total_spent_last_month",
        params: { amount },
      };
    }
  }

  if (type === "team_invitation") {
    return { kind: "i18n", key: "invitation_team_invite" };
  }

  if (type.startsWith("provider_post_notif_")) {
    return { kind: "raw", text: content };
  }

  return { kind: "i18n", key: content };
}
